import {
	ioJsonDeleteItemAction,
	ioJsonFetchAction,
	ioJsonFetchItemAction,
	ioJsonSaveAction
} from 'Core/store/actions/io';
import {hideLoading, hideLoadingFunction, showLoading} from "Core/helpers/loading";
import {getArray, isset} from "Core/helpers/data";
import {get} from "lodash";
import {actionCreators} from "Core/store/reducers";
import * as listItemDataMap from "./dataMap/listItem";
import * as itemFilterDataMap from "./dataMap/filter";
import * as itemDataMap from "./dataMap/item";
import {reducerStoreKey} from "./reducer";
import {isSuccessful} from "Core/helpers/io";

/**
 * Reset customer groups list Redux state to initial state
 * @return {(function(*))|*}
 */
export const resetCustomerGroupsListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].reset());
};

/**
 * Fetch customer groups list
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} [projectId] - ID of the project (customers are assigned to projects) hat will be used as a filter.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchCustomerGroupsListAction = (
	abortCallback, projectId, filter = null, pageNo = 1, perPage, sortBy, sortDir
) => dispatch => {
	let filterToUse = filter;
	if (!!projectId) {
		filterToUse = filter || {};
		filterToUse = {...filterToUse, projectId};
	}
	
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		'customer-group/search',
		'',
		filterToUse,
		null,
		pageNo,
		perPage,
		sortBy ? sortBy : null,
		sortDir
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: itemFilterDataMap.input(get(responseData, 'filter'), !!projectId),
					data: getArray(responseData, 'data').map(i => listItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load customer groups list into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} [projectId] - ID of the project (customers are assigned to projects) hat will be used as a filter.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. The API defines sort fields.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay is rendered.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadCustomerGroupsListAction = (
	abortCallback, projectId, filter = null, pageNo = 1, perPage, sortBy, sortDir, loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#main-page-table") : null);
	return fetchCustomerGroupsListAction(abortCallback, projectId, filter, pageNo, perPage, sortBy, sortDir)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setCustomerGroupsListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Set a customer groups list filter in Redux store
 *
 * @param {Object} filter - Filter to set.
 * @return {(function(*): void)|*}
 */
export const setCustomerGroupsFilterAction = filter => dispatch => {
	dispatch(actionCreators[reducerStoreKey].setCustomerGroupsListFilter(filter));
};

/**
 * Clear customer groups list from Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearCustomerGroupsListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearCustomerGroupsListData());
}

/**
 * Fetch customer groups item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string|number} id - DB ID of the customer groups item to fetch.
 * @return {function(*=): Promise<CustomerGroupsItemDataObject|undefined>}
 */
export const fetchCustomerGroupsItemAction = (abortCallback, id) => dispatch => {
	return ioJsonFetchItemAction(
		abortCallback,
		'defaultAuthorizedApi',
		'customer-group/fetch-by-id',
		id,
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => isSuccessful(responseData) ? itemDataMap.input(responseData?.data) : undefined);
};

/**
 * Load customer groups item into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} id - DB ID of the customer groups item to load.
 * @return {function(*=): Promise<CustomerGroupsItemDataObject|undefined>}
 */
export const loadCustomerGroupsItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#item-popup');
	return fetchCustomerGroupsItemAction(abortCallback, id)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setCustomerGroupsItem(responseData));
			hideLoading(loading);
			return responseData;
		});
}

/**
 * Clear customer groups item from Redux store
 * @return {(function(*=): void)|*}
 */
export const clearCustomerGroupsItemAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearCustomerGroupsItem());
};

/**
 * Create customer groups item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {CustomerGroupsItemDataObject} item - Customer groups item to save.
 * @return {function(*=): Promise<CustomerGroupsItemDataObject>} Promise that will resolve with the created item
 * received from IO or undefined if creation failed.
 */
export const createCustomerGroupsItemAction = (abortCallback, item) => dispatch => {
	const loading = showLoading('#item-popup');
	return ioJsonSaveAction(
		// @note abortCallback is set to undefined because save actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'customer-group/create',
		{
			id: '',
			data: itemDataMap.output(item)
		},
		undefined,
		true,
		hideLoadingFunction(loading)
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => isset(responseData) ? itemDataMap.input(responseData.data) : undefined);
};

/**
 * Update customer groups item
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} id - DB ID of the customer groups item to update.
 * @param {CustomerGroupsItemDataObject} item - Customer groups item to save.
 * @return {function(*=): Promise<CustomerGroupsItemDataObject>} Promise that will resolve with the updated item
 * received from IO or undefined if updating failed.
 */
export const updateCustomerGroupsItemAction = (abortCallback, id, item) => dispatch => {
	const loading = showLoading('#item-popup');

	return ioJsonSaveAction(
		// @note abortCallback is set to undefined because save actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'customer-group/update-by-id',
		{
			id,
			data: itemDataMap.output(item)
		},
		undefined,
		true,
		hideLoadingFunction(loading)
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => isset(responseData) ? itemDataMap.input(responseData.data) : undefined)
		// Set the current item to Redux store so that changes will be detected once the item has been updated
		.then(updatedItem => {
			if (isset(updatedItem)) dispatch(actionCreators[reducerStoreKey].setCustomerGroupsItem(item));
			return updatedItem;
		})
		// Save the updated item to Redux store received from response (since 'requestSavedData' is set to true)
		.then(updatedItem => {
			if (isset(updatedItem)) dispatch(actionCreators[reducerStoreKey].setCustomerGroupsItem(updatedItem));
			return updatedItem;
		});
};

/**
 * Delete customer groups item action
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string|number} id - ID of the customer groups item that will be deleted.
 * @return {function(*): Promise<IoJsonResponseObject>}
 */
export const deleteCustomerGroupsItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#item-delete-dialog .dialog-content-component .buttons');
	return ioJsonDeleteItemAction(
		// @note abortCallback is set to undefined because delete actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'customer-group/delete-by-ids',
		id,
		{},
		hideLoadingFunction(loading)
	)(dispatch);
};